import './photo-upload.scss';

import { notification } from 'antd';
import SkeletonImage from 'antd/es/skeleton/Image';
import { useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';

import { CustomModal } from '@/components/Shared/Modal/CustomModal';
import { Button } from '@/components/Shared/ui';
import { downloadFilesApi } from '@/integrations/index.api';
import { DeleteOutlined } from '@/media/antd-icons';
import CameraIcon from '@/media/icons/camera.svg?react';
import { handleError } from '@/services/handleError';
import { convertFileToBase64 } from '@/services/helpers/files';
import type { FileType } from '@/types/uploadFiles';

interface IProps {
  title: string;
  file?: FileType;
  onChange: (file: Partial<FileType>) => void;
  onDelete: () => void;
  helperText?: string;
  error?: string;
  isFrozen?: boolean;
  isRequired?: boolean;
}

const allowedExtensions = ['jpg', 'jpeg'];

const PhotoUpload: React.FC<IProps> = ({
  title,
  file,
  onChange,
  onDelete,
  helperText,
  error,
  isFrozen,
  isRequired
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const webCamRef = useRef<Webcam>(null);

  const [initialFile, setInitialFile] = useState<Partial<FileType>>();
  const [camOn, setCamOn] = useState<boolean>(false);
  const [showUploadTypeModal, setShowUploadTypeModal] =
    useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  const [fileLoading, setFileLoading] = useState(false);

  const handleRemoveFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (initialFile) {
      onChange(initialFile);
    } else {
      setSelectedFile(null);
      onDelete();
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!allowedExtensions.includes(fileExtension || '')) {
        notification.error({
          title: 'Недопустимое расширение файла!',
          description: 'Пожалуйста, загрузите картинку в формате .jpeg, .jpg'
        });
        return;
      }

      const base64String = (await convertFileToBase64(file))[1];
      setSelectedFile(base64String);
      onChange({ base64: base64String, name: file.name, size: file.size });
      setShowUploadTypeModal(false);
    }
  };

  const handleFileUploadClick = () => {
    setShowUploadTypeModal(true);
  };

  const stopCam = () => {
    setCamOn(false);
    setTimeout(() => {
      setShowUploadTypeModal(false);
    }, 0);
  };

  const handleTakeScreenShot = async () => {
    const imageSrc = webCamRef.current?.getScreenshot();
    if (imageSrc) {
      const base64 = imageSrc.split(',')[1];
      const base64Response = await fetch(`${imageSrc}`);
      const blob = await base64Response.blob();
      const imageFile = new File([blob], 'photo.jpeg');
      setSelectedFile(base64);
      onChange({ base64, name: imageFile.name, size: imageFile.size });
      stopCam();
    }
  };

  useEffect(() => {
    setCamOn(showUploadTypeModal);
  }, [showUploadTypeModal]);

  useEffect(() => {
    // Подгрузка файла и установка его в нужном формате в состояние формы
    const fetchBase64 = async () => {
      if (!(file && 'uuid' in file && file?.uuid)) return;
      setFileLoading(true);

      try {
        const fileUuid = 'uuid' in file ? (file.uuid as string) : '';
        const response = await downloadFilesApi.downloadBankFile(
          fileUuid || ''
        );

        const base64Response = await fetch(
          `data:image/webp;base64,${response.base64}`
        );
        const blob = await base64Response.blob();
        const imageFile = new File([blob], response.name);

        const tempInitialFile: Partial<FileType> = {
          base64: response.base64,
          name: imageFile.name,
          size: imageFile.size
        };
        setInitialFile(tempInitialFile);
        onChange(tempInitialFile);
      } catch (error) {
        handleError(error);
      }
      setFileLoading(false);
    };

    if (file && 'uuid' in file && file?.uuid) {
      fetchBase64();
      setSelectedFile(file?.base64);
    }
  }, [file]);

  if (fileLoading) {
    return (
      <SkeletonImage
        active
        classNames={{
          content: 'photo-upload__skeleton'
        }}
      />
    );
  }

  return (
    <div className="photo-upload">
      {!isFrozen && (
        <input
          ref={inputRef}
          onChange={handleFileChange}
          className="photo-upload__field"
          type="file"
          accept=".jpeg,.jpg"
        />
      )}

      {!file?.name && !isFrozen ? (
        <div
          className={`photo-upload__view photo-upload__shape ${
            error ? 'photo-upload__shape--error' : ''
          }`}
          onClick={handleFileUploadClick}
        >
          <p className="photo-upload__title">
            {title}
            {isRequired && <span className="photo-upload__required">*</span>}
          </p>

          <div className="photo-upload__main">
            <span className="photo-upload__main-icon">
              <CameraIcon />
            </span>
            <p className="photo-upload__text">
              Нажмите на иконку камеры, чтобы сделать фотографию
            </p>
            {error && (
              <p className="photo-upload__additional-text photo-upload__additional-text--error">
                {error}
              </p>
            )}
          </div>
        </div>
      ) : (
        file?.base64 && (
          <div className="photo-upload__selected photo-upload__shape">
            <img
              src={`data:image/webp;base64,${file.base64}`}
              alt="uploaded_photo"
              className="photo-upload__image"
            />
            {!isFrozen && (
              <button
                onClick={handleFileUploadClick}
                type="button"
                className="photo-upload__remake-photo"
              >
                <CameraIcon />
                Переснять
              </button>
            )}
            {!isFrozen && initialFile?.base64 !== file.base64 && (
              <button
                onClick={handleRemoveFile}
                type="button"
                className="photo-upload__delete-photo"
              >
                <DeleteOutlined />
              </button>
            )}
          </div>
        )
      )}

      {helperText && (
        <p className="photo-upload__additional-text">{helperText}</p>
      )}

      <CustomModal
        title="Выберите способ загрузки"
        handleCancel={stopCam}
        handleOk={() => setShowUploadTypeModal(false)}
        editClass="photo-upload-modal"
        isModalOpen={showUploadTypeModal}
        footer={[
          <Button type="button" key="cancel" onClick={stopCam}>
            Отмена
          </Button>,
          <Button
            type="button"
            key="upload"
            variant="filled-red"
            onClick={() => {
              stopCam();
              if (inputRef.current) inputRef.current.click();
            }}
          >
            Загрузить файл
          </Button>
        ]}
      >
        <div className="photo-upload-modal__content">
          {camOn ? (
            <Webcam
              screenshotFormat={'image/jpeg'}
              ref={webCamRef}
              width="100%"
              frameBorder={'10px'}
              audio={false}
              style={{ borderRadius: '10px 10px 0 0' }}
            />
          ) : selectedFile ? (
            <img
              src={`data:image/webp;base64,${selectedFile}`}
              alt="you photo"
            />
          ) : null}
          <Button
            type="button"
            variant="filled-red"
            className="photo-upload-modal__take-photo"
            onClick={handleTakeScreenShot}
          >
            <span className="photo-upload__main-icon">
              <CameraIcon />
            </span>
          </Button>
        </div>
      </CustomModal>
    </div>
  );
};

export default PhotoUpload;
